import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    List,
    ListItem,
    ListItemButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import api from '../../../../services/api';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    submitted: { label: 'Enviada', color: 'warning' },
    approved: { label: 'Aprovada', color: 'success' },
    rejected: { label: 'Rejeitada', color: 'error' }
};

const QUOTATION_STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    sent: { label: 'Enviada', color: 'warning' },
    received: { label: 'Recebida', color: 'info' },
    won: { label: 'Vencedora', color: 'success' },
    lost: { label: 'Perdedora', color: 'error' }
};

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const ViewPurchaseRequest = () => {
    const { id } = useParams();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [purchaseRequest, setPurchaseRequest] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [working, setWorking] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    const [quotations, setQuotations] = useState([]);
    const [comparisonRows, setComparisonRows] = useState([]);

    const [newQuotationDialogOpen, setNewQuotationDialogOpen] = useState(false);
    const [orgSearch, setOrgSearch] = useState('');
    const [orgResults, setOrgResults] = useState([]);
    const [showOrgResults, setShowOrgResults] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [validUntil, setValidUntil] = useState('');
    const [creatingQuotation, setCreatingQuotation] = useState(false);

    // Protege contra setState depois de desmontar - esta tela dispara uma
    // cadeia de requisições encadeadas (solicitação -> cotações/comparação)
    // que pode resolver depois do componente já ter saído de tela numa
    // navegação rápida (ex: "Nova Cotação" leva pra outra rota antes do
    // fetch anterior terminar).
    const mountedRef = useRef(true);
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            if (mountedRef.current) setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId || !id || id === 'new') return;
        carregarSolicitacao();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    const carregarSolicitacao = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/purchase_requests/${id}/`);
            if (!mountedRef.current) return;
            setPurchaseRequest(res.data);
            if (res.data.status === 'approved') {
                carregarCotacoes();
                carregarComparacao();
            }
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: 'Erro ao carregar solicitação' });
        }
    };

    const carregarCotacoes = async () => {
        const res = await api.get(`v1/apps/${appId}/purchase_quotations/?purchase_request=${id}`);
        if (mountedRef.current) setQuotations(res.data.quotations || []);
    };

    const carregarComparacao = async () => {
        const res = await api.get(`v1/apps/${appId}/purchase_requests/${id}/comparison/`);
        if (mountedRef.current) setComparisonRows(res.data.rows || []);
    };

    const handleOrgSearch = useMemo(
        () =>
            debounce(async (value, currentAppId) => {
                if (!value || !currentAppId) {
                    if (mountedRef.current) {
                        setOrgResults([]);
                        setShowOrgResults(false);
                    }
                    return;
                }
                const res = await api.get(`v1/apps/${currentAppId}/organizations/?search=${encodeURIComponent(value)}`);
                if (mountedRef.current) {
                    setOrgResults(res.data.organizations || []);
                    setShowOrgResults(true);
                }
            }, 400),
        []
    );

    const handleOrgSearchChange = (e) => {
        const value = e.target.value;
        setOrgSearch(value);
        setSelectedOrg(null);
        handleOrgSearch(value, appId);
    };

    const selectOrg = (org) => {
        setSelectedOrg(org);
        setOrgSearch(org.name);
        setShowOrgResults(false);
    };

    const handleCreateQuotation = async () => {
        if (!selectedOrg) {
            setStatus({ type: 'error', message: 'Selecione um fornecedor' });
            return;
        }
        setCreatingQuotation(true);
        try {
            const res = await api.post(`v1/apps/${appId}/purchase_quotations/`, {
                purchase_request: id,
                organization: selectedOrg.id,
                valid_until: validUntil || undefined
            });
            history.push(`/dashboard/purchasing/quotations/${res.data.id}/`);
        } catch (error) {
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar cotação' });
        } finally {
            if (mountedRef.current) setCreatingQuotation(false);
        }
    };

    const handleMarkWinner = async (itemId, quotationId) => {
        await api.post(`v1/apps/${appId}/purchase_quotations/${quotationId}/items/${itemId}/mark-winner/`, {});
        if (!mountedRef.current) return;
        carregarComparacao();
        carregarCotacoes();
    };

    const handleGenerateOrder = async () => {
        if (!window.confirm('Gerar pedido(s) de compra a partir dos itens vencedores?')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/purchase_requests/${id}/generate-order/`, {});
            const orders = res.data.orders || [];
            if (mountedRef.current) {
                setStatus({
                    type: 'success',
                    message:
                        orders.length > 0 ? `${orders.length} pedido(s) de compra gerado(s).` : 'Nenhum item vencedor pendente para gerar pedido.'
                });
            }
        } catch (error) {
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao gerar pedido' });
        } finally {
            if (mountedRef.current) setWorking(false);
        }
    };

    const handleSubmit = async () => {
        if (!window.confirm('Enviar esta solicitação para aprovação? Depois disso ela não pode mais ser editada.')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/purchase_requests/${id}/submit/`, {});
            if (mountedRef.current) {
                setPurchaseRequest(res.data);
                setStatus({ type: 'success', message: 'Solicitação enviada para aprovação.' });
            }
        } catch (error) {
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao enviar solicitação' });
        } finally {
            if (mountedRef.current) setWorking(false);
        }
    };

    const handleApprove = async () => {
        if (!window.confirm('Aprovar esta solicitação?')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/purchase_requests/${id}/approve/`, {});
            if (mountedRef.current) {
                setPurchaseRequest(res.data);
                setStatus({ type: 'success', message: 'Solicitação aprovada.' });
            }
        } catch (error) {
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao aprovar solicitação' });
        } finally {
            if (mountedRef.current) setWorking(false);
        }
    };

    const handleReject = async () => {
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/purchase_requests/${id}/reject/`, { rejection_reason: rejectionReason });
            if (mountedRef.current) {
                setPurchaseRequest(res.data);
                setStatus({ type: 'success', message: 'Solicitação rejeitada.' });
                setRejectDialogOpen(false);
            }
        } catch (error) {
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao rejeitar solicitação' });
        } finally {
            if (mountedRef.current) setWorking(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Excluir esta solicitação em rascunho? Essa ação não pode ser desfeita.')) return;
        setWorking(true);
        try {
            await api.delete(`v1/apps/${appId}/purchase_requests/${id}/`);
            history.push('/dashboard/purchasing/requests');
        } catch (error) {
            if (mountedRef.current) {
                setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir solicitação' });
                setWorking(false);
            }
        }
    };

    if (!purchaseRequest) {
        return <Box>{status.message && <Alert severity={status.type}>{status.message}</Alert>}</Box>;
    }

    const statusInfo = STATUS_LABEL[purchaseRequest.status] || { label: purchaseRequest.status, color: 'default' };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h3">Solicitação de Compra</Typography>
                <Chip label={statusInfo.label} color={statusInfo.color} />
            </Stack>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                                Solicitado por
                            </Typography>
                            <Typography>{purchaseRequest.requested_by_name || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                                Criada em
                            </Typography>
                            <Typography>{new Date(purchaseRequest.created_at).toLocaleString('pt-BR')}</Typography>
                        </Grid>
                        {purchaseRequest.justification && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Justificativa
                                </Typography>
                                <Typography>{purchaseRequest.justification}</Typography>
                            </Grid>
                        )}
                        {purchaseRequest.notes && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Observações
                                </Typography>
                                <Typography>{purchaseRequest.notes}</Typography>
                            </Grid>
                        )}
                        {purchaseRequest.status === 'rejected' && purchaseRequest.rejection_reason && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Motivo da rejeição
                                </Typography>
                                <Typography color="error.main">{purchaseRequest.rejection_reason}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Quantidade</TableCell>
                                <TableCell align="right">Custo estimado</TableCell>
                                <TableCell align="right">Subtotal estimado</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {purchaseRequest.items?.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.produto_variacao_nome || item.service_nome}</TableCell>
                                    <TableCell align="right">{item.quantidade}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.valor_unitario_estimado)}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.subtotal_estimado)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box textAlign="right" mt={2}>
                        <Typography variant="h5">Total estimado: {formatCurrencyBR(purchaseRequest.total_estimado)}</Typography>
                    </Box>
                </CardContent>
            </Card>

            {purchaseRequest.status === 'approved' && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h5">Cotações</Typography>
                            <Button variant="outlined" onClick={() => setNewQuotationDialogOpen(true)}>
                                Nova Cotação
                            </Button>
                        </Stack>

                        {quotations.length === 0 ? (
                            <Typography color="text.secondary">Nenhuma cotação criada ainda.</Typography>
                        ) : (
                            <Table size="small" sx={{ mb: 2 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fornecedor</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {quotations.map((q) => {
                                        const qStatusInfo = QUOTATION_STATUS_LABEL[q.status] || { label: q.status, color: 'default' };
                                        return (
                                            <TableRow key={q.id}>
                                                <TableCell>{q.organization_name}</TableCell>
                                                <TableCell>
                                                    <Chip label={qStatusInfo.label} color={qStatusInfo.color} size="small" />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        component={RouterLink}
                                                        to={`/dashboard/purchasing/quotations/${q.id}/`}
                                                    >
                                                        Abrir
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}

                        {comparisonRows.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                                    Comparação de Preços
                                </Typography>
                                {comparisonRows.map((row) => (
                                    <Box key={row.produto_variacao || row.service} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {row.produto_variacao_nome || row.service_nome}
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Fornecedor</TableCell>
                                                    <TableCell align="right">Preço unitário</TableCell>
                                                    <TableCell align="right">Prazo (dias)</TableCell>
                                                    <TableCell align="right" />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {row.ofertas.map((oferta) => (
                                                    <TableRow key={oferta.item_id}>
                                                        <TableCell>{oferta.organization_name}</TableCell>
                                                        <TableCell align="right">{formatCurrencyBR(oferta.valor_unitario_cotado)}</TableCell>
                                                        <TableCell align="right">{oferta.prazo_entrega_dias ?? '-'}</TableCell>
                                                        <TableCell align="right">
                                                            {oferta.is_winner ? (
                                                                <Chip label="Vencedor" color="success" size="small" />
                                                            ) : oferta.quotation_status === 'received' ? (
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => handleMarkWinner(oferta.item_id, oferta.quotation_id)}
                                                                >
                                                                    Marcar vencedor
                                                                </Button>
                                                            ) : null}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                ))}

                                <Box textAlign="right" mt={2}>
                                    <Button variant="contained" disabled={working} onClick={handleGenerateOrder}>
                                        Gerar Pedido(s) de Compra
                                    </Button>
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={() => history.push('/dashboard/purchasing/requests')}>Voltar</Button>

                {purchaseRequest.status === 'draft' && (
                    <Button color="error" variant="outlined" disabled={working} onClick={handleDelete}>
                        Excluir
                    </Button>
                )}

                {purchaseRequest.status === 'draft' && (
                    <Button variant="contained" disabled={working} onClick={handleSubmit}>
                        Enviar para Aprovação
                    </Button>
                )}

                {purchaseRequest.status === 'submitted' && (
                    <>
                        <Button color="error" variant="outlined" disabled={working} onClick={() => setRejectDialogOpen(true)}>
                            Rejeitar
                        </Button>
                        <Button variant="contained" disabled={working} onClick={handleApprove}>
                            Aprovar
                        </Button>
                    </>
                )}
            </Box>

            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Rejeitar solicitação</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Motivo da rejeição"
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ mt: 1 }}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={handleReject} disabled={working}>
                        Rejeitar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={newQuotationDialogOpen} onClose={() => setNewQuotationDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nova Cotação</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Box sx={{ position: 'relative' }}>
                            <TextField
                                label="Fornecedor"
                                fullWidth
                                required
                                value={orgSearch}
                                onChange={handleOrgSearchChange}
                                onFocus={() => orgResults.length > 0 && setShowOrgResults(true)}
                                placeholder="Buscar por nome ou CNPJ..."
                            />
                            {showOrgResults && orgResults.length > 0 && (
                                <Paper sx={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: 240, overflowY: 'auto' }}>
                                    <List dense>
                                        {orgResults.map((org) => (
                                            <ListItem key={org.id} disablePadding>
                                                <ListItemButton onClick={() => selectOrg(org)}>
                                                    {org.name} {org.cnpj ? `- ${org.cnpj}` : ''}
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            )}
                        </Box>
                        <TextField
                            label="Validade da proposta (opcional)"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={validUntil}
                            onChange={(e) => setValidUntil(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewQuotationDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleCreateQuotation} disabled={creatingQuotation}>
                        {creatingQuotation ? 'Criando...' : 'Criar Cotação'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ViewPurchaseRequest;
